import { Group, Menu, UnstyledButton, Avatar, Text, Button, Image, Box, Burger, Drawer, Stack, Container } from '@mantine/core';
import { IconChevronDown, IconLogout, IconSettings, IconChartBar, IconDashboard, IconReport, IconBuildingStore } from '@tabler/icons-react';
import { pb } from '../../lib/pocketbase';
import { Link, useLocation } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';

interface HeaderProps {
  user: any;
  onLogout: () => void;
  appTitle: string;
  logoUrl?: string;
}

export function Header({ user, onLogout, appTitle, logoUrl }: HeaderProps) {
  const location = useLocation();
  const [opened, { toggle, close }] = useDisclosure(false);
  
  // Get the user's avatar URL if it exists
  const avatarUrl = user?.avatar ? pb.files.getURL(user, user.avatar) : '';

  // Check if user is admin (role_level 1)
  const isAdmin = user?.role_level === 1;

  const navigationLinks = [
    { to: '/dashboard', icon: <IconDashboard size={16} />, label: 'Dashboard' },
    { to: '/berichte', icon: <IconReport size={16} />, label: 'Berichte' },
    { to: '/statistik', icon: <IconChartBar size={16} />, label: 'Statistik' },
    ...(isAdmin ? [
      { to: '/bereiche', icon: <IconBuildingStore size={16} />, label: 'Bereiche' },
      { to: '/settings', icon: <IconSettings size={16} />, label: 'Einstellungen' }
    ] : []),
  ];

  const NavButton = ({ to, icon, label, mobile = false }: { to: string; icon: React.ReactNode; label: string; mobile?: boolean }) => (
    <Button
      component={Link}
      to={to}
      variant={location.pathname === to ? 'filled' : 'subtle'}
      leftSection={icon}
      size={mobile ? "md" : "sm"}
      fullWidth={mobile}
      onClick={mobile ? close : undefined}
    >
      {label}
    </Button>
  );

  return (
    <Container fluid px="xs">
      <Group justify="space-between" h={70}>
        <Group>
          {/* Logo/Title area */}
          <Box style={{ maxWidth: '200px', display: 'flex', alignItems: 'center' }}>
            {logoUrl ? (
              <Image
                src={logoUrl}
                h={60}
                fit="contain"
                style={{ display: 'block' }}
              />
            ) : (
              <Text size="lg" fw={500}>{appTitle}</Text>
            )}
          </Box>

          {/* Desktop & Tablet Navigation */}
          <Group gap="xs" visibleFrom="xs">
            {navigationLinks.map((link) => (
              <NavButton key={link.to} {...link} />
            ))}
          </Group>
        </Group>

        <Group>
          {/* User Menu */}
          <Menu
            width={200}
            position="bottom-end"
            transitionProps={{ transition: 'pop-top-right' }}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group gap={7}>
                  <Avatar size={30} radius="xl" src={avatarUrl} />
                  <Text fw={500} size="sm" lh={1} mr={3} visibleFrom="xs">
                    {user?.email}
                  </Text>
                  <IconChevronDown size={12} stroke={1.5} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                color="red"
                leftSection={<IconLogout size={14} />}
                onClick={onLogout}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          {/* Mobile Navigation Burger - Only visible on extra small screens */}
          <Burger opened={opened} onClick={toggle} hiddenFrom="xs" size="sm" />
        </Group>
      </Group>

      {/* Mobile Navigation Drawer - Only for extra small screens */}
      <Drawer
        opened={opened}
        onClose={close}
        size="100%"
        padding="md"
        title="Navigation"
        hiddenFrom="xs"
        position="right"
      >
        <Stack>
          {navigationLinks.map((link) => (
            <NavButton key={link.to} {...link} mobile />
          ))}
        </Stack>
      </Drawer>
    </Container>
  );
}
